import type { CanvasElementId, ColumnId, WithSlots } from '@blaze-runtime/engine-domain';
import {
  type ConditionalFieldParams,
  ELEMENT_PATH_WF,
  type IPropEditorResolver,
  type PropertiesConfig,
  type PropertiesConfigItemDefinition,
  type PropertiesConfigSection,
  type PropertiesConfigSubsection,
  WIZARD_ACTION_OPTIONS,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import {
  validateFontSizeWithLineHeight,
  validateLineHeightWithFontSize,
} from '../../utils/elements/validation.utils';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { wizardQuestionsField } from './wizard-questions.props-config';
import { wizardStepsField } from './wizard-steps.props-config';
import { getInitialFormSteps, getInitialQuestionnaireSteps } from './wizard.constants';
import { WIZARD_MODE_PATH, WIZARD_STEPS_ITEMS_PATH, type WizardPropsModel } from './wizard.models';
import { type WizardTextStyleGroup } from './wizard.utils';

export function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('wizard', [
    epc.section('content', 'Content', [
      epc.subsections.subsection(
        'data',
        'Source',
        [
          epc.fields.selectDataSourceField(
            { key: 'value', label: '', sources: 'list', bindings: [] },
            'form-group-action',
            '',
            {
              hideLabel: false,
              hasRefresh: true,
              disableWhen: (
                model: WizardPropsModel & { root: WithSlots<ColumnId> & { id: CanvasElementId } },
                params?: ConditionalFieldParams,
              ): string | undefined => {
                if (
                  model.content.mode !== 'questionnaire' &&
                  model.content.steps?.items.length === 0
                ) {
                  return 'At least one Step is required to set Data Source';
                }
                const slotId =
                  params?.dataFeatures?.elementType !== 'wizard'
                    ? undefined
                    : (params?.dataFeatures.selectedSlotId as ColumnId);
                const slotIndex = slotId === undefined ? 0 : model.root.slots.ids.indexOf(slotId);
                if (model.content.steps?.items[slotIndex]?.type === 'review') {
                  return 'Cannot add main Form into Review step';
                }
                return undefined;
              },
            },
          ),
          epc.fields.textInfo('', 'New data will trigger this action in the table', {
            header: true,
          }),
          epc.fields.selectFieldFromList('content.action', '', WIZARD_ACTION_OPTIONS, {
            hideLabel: true,
            showSelection: true,
            allowNone: false,
          }),
        ],
        { hideLabel: true },
      ),

      epc.subsections.subsection('content.mode', 'Mode', [
        epc.utils.observedField(
          epc.fields.radiosField(
            WIZARD_MODE_PATH,
            'Mode',
            [
              { value: 'form', label: 'Form' },
              { value: 'questionnaire', label: 'Questionnaire' },
            ],
            { hideLabel: true },
          ),
          (_, value) => {
            // Both modes share content.steps.items - switching resets them, since Form steps
            // (multi-field forms) and Questionnaire steps (a one-field form each) mean
            // different things. Each mode's seed keeps the confirmation step every wizard
            // must have; this is its only writer, which is what lets the questionnaire
            // regeneration stay a pure function of the steps rather than asserting one.
            // handleWizardFormModeSlotsRebuild$ rebuilds the slots for the Form direction;
            // the other direction is rebuilt by the questionnaire regeneration this reset
            // triggers.
            // NOTE: keys here must be flat props paths (see handleValueObservers), not a
            // nested object - a nested object would overwrite the whole `content` prop.
            return value === 'form'
              ? {
                  'content.questionsEnabled': [],
                  [WIZARD_STEPS_ITEMS_PATH]: getInitialFormSteps(),
                }
              : {
                  'content.questionsEnabled': [],
                  [WIZARD_STEPS_ITEMS_PATH]: getInitialQuestionnaireSteps(),
                };
          },
        ),
        epc.fields.textInfo(
          '',
          'Collect input across one or more steps, each with multiple fields.',
          {
            header: true,
            hideWhen: (model) => model.content.mode === 'questionnaire',
          },
        ),
        epc.fields.textInfo('', 'Present each field as a single question, one at a time.', {
          header: true,
          hideWhen: (model) => model.content.mode === 'form',
        }),
      ]),

      epc.subsections.subsection(
        'content.questions',
        'Questions',
        [
          wizardQuestionsField(WIZARD_STEPS_ITEMS_PATH, 'Questions'),
          epc.fields.textInfo('', `Map the column for each step's question.`, {
            header: true,
            hideWhen: (model) =>
              model.content.mode === 'form' || model.content.steps?.items.length !== 0,
          }),
        ],
        {
          hideLabel: true,
          hideWhen: (_: string, __: IPropEditorResolver, model: WizardPropsModel) =>
            model.content.mode === 'form',
        },
      ),

      epc.subsections.subsection(
        'content.steps',
        'Steps',
        [wizardStepsField(WIZARD_STEPS_ITEMS_PATH, 'Steps')],
        {
          hideLabel: true,
          hideWhen: (_: string, __: IPropEditorResolver, model: WizardPropsModel) =>
            model.content.mode === 'questionnaire',
        },
      ),

      epc.subsections.subsection('content.layout', 'Options', [
        epc.fields.textInfo('', 'Style', { header: true }),
        epc.fields.selectFieldFromList(
          'content.layout.style',
          '',
          [
            { label: 'Numbered Steps', value: 'numbers' },
            { label: 'Progress Lines', value: 'lines' },
            { label: 'Progress Bar', value: 'bar' },
            { label: 'Tabs', value: 'tabs' },
          ],
          { allowNone: false, hideLabel: true },
        ),
        epc.fields.toggleField('content.layout.progressPercent.enabled', 'Show Percent', {
          hideWhen: (model) => model.content.layout.style !== 'bar',
        }),
        progressPercentPositionField('top', 'icon_aligns_to_left_16', 'icon_aligns_to_right_16'),
        progressPercentPositionField('side', 'icon_aligns_to_top_16', 'icon_aligns_to_bottom_16'),
        epc.fields.toggleField('content.layout.numerateTabs', 'Numerate Steps', {
          hideWhen: (model) =>
            model.content.layout.style !== 'tabs' && model.content.layout.style !== 'lines',
        }),
        epc.fields.radiosField('content.layout.placement', 'Navigation', [
          { value: 'top', label: 'Top' },
          { value: 'side', label: 'Side' },
        ]),
        ...epc.utils.conditionalFields(
          [
            epc.fields.alignField('content.layout.align', 'Align', {
              vertical: false,
              justify: true,
            }),
          ],
          'content.layout.placement',
          (placement: WizardPropsModel['content']['layout']['placement'], model) =>
            placement !== 'top' || model.content.layout.style === 'bar',
        ),
        epc.fields.toggleField('content.saveDraftEnabled', 'Save Draft', {
          hideWhen: (model) => model.content.action !== 'add',
        }),
      ]),

      epc.subsections.subsection(
        'content',
        'Action',
        [epc.fields.buttonActionField('', 'Action on submit')],
        {
          hideLabel: true,
          hideWhen: (elementId: string, resolver: IPropEditorResolver) =>
            !resolver.resolveValue('ELEMENT_PATH_VALUE', {
              path: `${ELEMENT_PATH_WF}id`,
              elementId,
            }),
        },
      ),

      epc.subsections.visibilitySubsection(),

      epc.subsections.titleContentSubsection('content'),
    ]),

    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Padding & Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
        ]),
        epc.subsections.subsection('style.buttons', 'Buttons', [
          epc.fields.radiosField(
            'style.buttons.buttonsView',
            '',
            [
              { value: 'primary', label: 'Primary' },
              { value: 'secondary', label: 'Secondary' },
            ],
            { hideLabel: true },
          ),
          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.buttons.primary.text', 'Label', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.fields.colorField('style.buttons.primary.fill', 'Fill', {}),
              epc.fields.colorField('style.buttons.primary.outline', 'Outline', {
                allowGradient: false,
              }),
            ],
            'style.buttons.buttonsView',
            (buttonsView: WizardPropsModel['style']['buttons']['buttonsView']) =>
              buttonsView !== 'primary',
          ),
          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.buttons.secondary.text', 'Label', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.fields.colorField('style.buttons.secondary.fill', 'Fill', {}),
              epc.fields.colorField('style.buttons.secondary.outline', 'Outline', {
                allowGradient: false,
              }),
            ],
            'style.buttons.buttonsView',
            (buttonsView: WizardPropsModel['style']['buttons']['buttonsView']) =>
              buttonsView !== 'secondary',
          ),
        ]),
        questionnaireTextStyleSubsection('question', 'Question'),
        questionnaireTextStyleSubsection('explanation', 'Explanation'),
        epc.subsections.elementBackgroundSubsection('style.container', 'Container', {
          hasOutlineSides: true,
        }),

        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.title.text',
          fontFamily: 'style.titleBlock.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'style.titleBlock.fontStyle',
          fontSize: 'style.titleBlock.fontSize',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}

/**
 * Where the Progress Bar shows its percentage. Two fields share the single
 * `content.layout.progressPercent.position` path because the icons have to match the bar's
 * orientation (a top bar reads left/right, a side bar top/bottom) and `positionField` takes
 * a static icon list - only the one matching the current navigation placement is ever visible.
 */
function progressPercentPositionField(
  placement: WizardPropsModel['content']['layout']['placement'],
  startIcon: string,
  endIcon: string,
): PropertiesConfigItemDefinition<'radios'> {
  return epc.fields.positionField(
    'content.layout.progressPercent.position',
    'Percent position',
    [
      { value: 'left', label: '', icon: startIcon },
      { value: 'right', label: '', icon: endIcon },
    ],
    { defaultValue: 'right' },
    {
      hideWhen: (model) => {
        const layout = model.content.layout;
        return layout.style !== 'bar' || layout.placement !== placement;
      },
    },
  );
}

/**
 * "Style All Fields"-like entry for the generated question/explanation Text elements: an
 * Edit button opening a dialog with the typography knobs and an "Overwrite Custom
 * Settings" trigger. Shown only when the questionnaire has questions.
 */
function questionnaireTextStyleSubsection(
  key: WizardTextStyleGroup,
  label: string,
): PropertiesConfigSubsection {
  return epc.subsections.subsection(
    `style.${key}`,
    '',
    [
      epc.fields.subConfigField(`style.${key}`, label, {
        dialogId: `GENERIC:WIZARD_${key.toUpperCase()}_STYLE`,
        dialogTitle: label,
        configFields: questionnaireTextStyleDialogConfig(key, label),
      }),
    ],
    {
      hideLabel: true,
      labelWidth: '70%',
      hideWhen: (_: string, __: IPropEditorResolver, model: WizardPropsModel) =>
        model.content.mode !== 'questionnaire' ||
        !model.content.steps?.items.some((step) => !!step?.columnKey),
    },
  );
}

/** Same field set the textArea element exposes for its font and text color. */
function questionnaireTextStyleDialogConfig(
  key: WizardTextStyleGroup,
  label: string,
): PropertiesConfigSection[] {
  const base = `style.${key}`;
  return [
    epc.section('style', label, [
      epc.subsections.subsection(`${base}.font`, 'Font', [
        ...epc.fields.fontFamilyAndWeightFields(
          `${base}.font.fontFamily`,
          `${base}.font.fontWeight`,
        ),
        epc.fields.fontStyleField(`${base}.font.style`, 'Style', ['bold']),
        epc.utils.observedField(
          epc.fields.sliderField(`${base}.font.fontSize`, 'Size', [
            { min: 8, max: 72, unit: 'px' },
          ]),
          (props: WizardPropsModel, value: ValueWithUnitOfMeasure) =>
            validateLineHeightWithFontSize(props, value, `${base}.font.lineHeight`),
        ),
        epc.utils.observedField(
          epc.fields.sliderField(`${base}.font.lineHeight`, 'Line height', [
            { min: 8, max: 72, unit: 'px' },
          ]),
          (props: WizardPropsModel, value: ValueWithUnitOfMeasure) =>
            validateFontSizeWithLineHeight(props, value, `${base}.font.fontSize`),
        ),
        epc.fields.sliderField(`${base}.font.letterSpacing`, 'Letter spacing', [
          { min: -10, max: 10, unit: 'px', step: 0.1 },
          { min: -2, max: 2, unit: 'rem', step: 0.1 },
          { min: -15, max: 15, unit: '%', step: 0.1 },
        ]),
      ]),
      epc.subsections.subsection(`${base}.textColor`, 'Text', [
        epc.fields.colorField(`${base}.textColor`, 'Color', {
          allowGradient: false,
          allowNone: false,
        }),
      ]),
      epc.subsections.triggerSubsection({
        label: 'Overwrite Custom Settings',
        action: 'APPLY_PARENT_STYLE',
        args: { childrenGroup: key },
      }),
    ]),
  ];
}
